/**
 * NOTES-V2 N5 — content signature pra dedup automático no read path.
 *
 * Assinatura = sha256(`${type}|${normalized_content[:200]}`)[:16].
 * Determinístico — mesma nota produz mesmo hash sempre. Whitespace
 * collapsing + lowercase normaliza variações triviais. 200 chars
 * é janela suficiente pra capturar dedup do agent chamando
 * notes_create 2x com mesmo content sem inflar custo.
 *
 * Hash é SÓ pra dedup no client — não persiste, não vai pro backend.
 * Web Crypto API já é usado em executionTrace.js, sem nova dep.
 *
 * Fallback se SubtleCrypto não existir (contexto não-seguro): retorna
 * o seed normalizado direto (igualdade ainda funciona, sem hashing).
 */

export async function contentSignature(content, type) {
  const normalized = String(content || "")
    .slice(0, 200)
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
  const seed = `${type || "general"}|${normalized}`;
  if (typeof crypto === "undefined" || !crypto.subtle) {
    return `fb:${seed.slice(0, 32)}`;
  }
  try {
    const buf = new TextEncoder().encode(seed);
    const hash = await crypto.subtle.digest("SHA-256", buf);
    return Array.from(new Uint8Array(hash))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("")
      .slice(0, 16);
  } catch {
    return `fb:${seed.slice(0, 32)}`;
  }
}

/**
 * Versão síncrona — útil pra usar dentro de useMemo sem precisar de
 * efeito + state pra resolver Promise. Tradeoff: usa simple djb2 hash
 * em vez de sha256. Same equality property (mesmo input → mesmo hash).
 * Não é cryptographic; é só pra dedup determinístico no client.
 */
export function contentSignatureSync(content, type) {
  const normalized = String(content || "")
    .slice(0, 200)
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
  const seed = `${type || "general"}|${normalized}`;
  // djb2 — rápido, determinístico, ~32 bits hex.
  let h = 5381;
  for (let i = 0; i < seed.length; i++) {
    h = ((h << 5) + h) ^ seed.charCodeAt(i);
  }
  // hex 8 chars sufix do seed pra reduzir colisão (mantém determinismo).
  const hex = (h >>> 0).toString(16).padStart(8, "0");
  const suffix = seed.slice(-8).replace(/[^a-z0-9]/gi, "_");
  return `${hex}_${suffix}`;
}

/**
 * Dedup automático no read path.
 *
 * Agrupa notas por contentSignature. Dentro da janela de DEDUP_WINDOW_MS,
 * mantém a MAIS RECENTE e anexa duplicatesHidden=N nela. Fora da janela,
 * todas aparecem.
 *
 * Razão da janela: agent chamando notes_create 2x num único turn cria
 * em <1s. Edição manual posterior do mesmo conteúdo (legítima) está
 * fora da janela. 30s cobre o caso adversarial sem prejudicar uso real.
 *
 * Input:  notes (array do GET /api/notes)
 * Output: { deduped: array, hiddenCount: number, signatures: Map(sig→note[]) }
 */
const DEFAULT_WINDOW_MS = 30 * 1000;

export function dedupNotes(notes, windowMs = DEFAULT_WINDOW_MS) {
  if (!Array.isArray(notes) || notes.length === 0) {
    return { deduped: [], hiddenCount: 0, signatures: new Map() };
  }
  // Pré-computa sig sync por nota.
  const withSig = notes.map((n) => ({
    note: n,
    sig: contentSignatureSync(n.content || "", n.type),
    ts: n.created_at ? new Date(n.created_at).getTime() : 0,
  }));
  // Sort por ts desc (mais recente primeiro). NaN/0 vai pro fim.
  withSig.sort((a, b) => (b.ts || 0) - (a.ts || 0));

  const sigGroups = new Map(); // sig -> [{note, ts}]
  for (const item of withSig) {
    const arr = sigGroups.get(item.sig) || [];
    arr.push(item);
    sigGroups.set(item.sig, arr);
  }

  const deduped = [];
  const visibleIds = new Set();
  let hiddenCount = 0;

  for (const [sig, items] of sigGroups.entries()) {
    if (items.length === 1) {
      deduped.push(items[0].note);
      visibleIds.add(items[0].note._id || items[0].note.id);
      continue;
    }
    // Agrupa SUB-bursts: cada burst começa do item mais recente e
    // colapsa qualquer outro item dentro de windowMs DESSE item.
    const remaining = [...items]; // já ordenado desc
    while (remaining.length > 0) {
      const head = remaining.shift();
      const headTs = head.ts;
      const burst = [head];
      // Pega os próximos que estão dentro da janela do head.
      for (let i = 0; i < remaining.length; ) {
        if (Math.abs(headTs - remaining[i].ts) <= windowMs) {
          burst.push(remaining.splice(i, 1)[0]);
        } else {
          i++;
        }
      }
      const winner = burst[0];
      const hiddenInBurst = burst.length - 1;
      hiddenCount += hiddenInBurst;
      const noteOut = { ...winner.note };
      if (hiddenInBurst > 0) {
        noteOut.duplicatesHidden = hiddenInBurst;
        noteOut.duplicatesHiddenIds = burst.slice(1).map(
          (b) => b.note._id || b.note.id,
        );
      }
      deduped.push(noteOut);
      visibleIds.add(noteOut._id || noteOut.id);
    }
  }

  // Restaura ordem original (cronológica desc).
  deduped.sort((a, b) => {
    const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
    const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
    return tb - ta;
  });

  return { deduped, hiddenCount, signatures: sigGroups };
}
